<template>
  <v-bottom-sheet v-model="visible" max-width="1920" class="fire-chart-detail-dialog">
    <div class="dialog-wrapper">
      <!-- 标题栏 -->
      <div class="dialog-title">
        <v-btn icon variant="text" class="back-btn" @click="close">
          <v-icon>mdi-reply-outline</v-icon>
          返回
        </v-btn>
        消防点位详情
      </div>

      <!-- 主内容区域 -->
      <div class="dialog-main">
        <!-- 筛选栏 -->
        <div class="filter-bar">
          <div class="filter-item">
            <label class="filter-label date-label">请选择日期</label>
            <el-date-picker v-model="dateRange" type="datetimerange" range-separator="至" start-placeholder="开始时间"
              end-placeholder="结束时间" format="YYYY-MM-DD HH:mm:ss" value-format="YYYY-MM-DD HH:mm:ss" :clearable="false"
              class="filter-date-picker" popper-class="fire-chart-date-popper" :default-time="defaultTime" />
          </div>

          <div class="filter-item">
            <label class="filter-label">设备</label>
            <v-select v-model="queryField" :items="fieldOptions" item-title="label" item-value="value" placeholder="请选择"
              variant="outlined" density="compact" class="filter-select"
              :menu-props="{ contentClass: 'fire-chart-filter-select-menu' }" hide-details />
          </div>

          <div class="filter-actions">
            <v-btn color="primary" class="query-btn" @click="handleQuery">
              <img src="@/assets/icons/svg/search-query.svg" alt="查询" class="query-icon"
                onerror="this.style.display='none';this.parentNode.querySelector('.fallback-icon').style.display='inline-block'" />
              <v-icon size="small" class="fallback-icon" style="display:none; margin-right:6px">mdi-magnify</v-icon>
              查询
            </v-btn>
          </div>
        </div>

        <!-- 图表区域 -->
        <div class="chart-wrapper">
          <div v-if="loading" class="loading-overlay">
            <v-progress-circular indeterminate color="primary"></v-progress-circular>
          </div>

          <!-- 图表容器 -->
          <div ref="chartRef" class="chart-container"></div>

          <!-- 暂无数据 -->
          <div v-if="!loading && noData" class="no-data">
            暂无数据
          </div>
        </div>
      </div>

      <!-- 关闭按钮 -->
      <v-btn icon size="small" variant="text" class="close-btn" @click="close">
        <v-icon>mdi-close</v-icon>
      </v-btn>
    </div>
  </v-bottom-sheet>
</template>

<script setup>
import { ref, computed, watch, nextTick, onUnmounted } from 'vue'
import * as echarts from 'echarts'
import request from '@/utils/request'
import dayjs from 'dayjs'

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false
  },
  stationId: {
    type: [Number, String],
    required: true
  },
  elinkUuid: {
    type: String,
    default: ''
  },
  initialField: {
    type: String,
    default: 'temp'
  }
})

const emit = defineEmits(['update:modelValue'])

const visible = computed({
  get: () => props.modelValue,
  set: (val) => emit('update:modelValue', val)
})

const loading = ref(false)
const chartRef = ref(null)
let chartInstance = null
const noData = ref(false)

// 筛选条件
const dateRange = ref([])
const queryField = ref('temp')

// 字段选项
const fieldOptions = [
  // { label: 'VOC浓度', value: 'voc_conc' },
  { label: '柜体温度', value: 'temp' },
  { label: '柜体湿度', value: 'hum' },
  { label: 'CO浓度', value: 'co_conc' },
  { label: '烟雾浓度', value: 'smoke_conc' }
]

// 默认时间设置
const defaultTime = [
  new Date(2000, 1, 1, 0, 0, 0),
  new Date(2000, 1, 1, 23, 59, 59),
]

// 初始化默认时间：今天0点到当前时间
const initDateRange = () => {
  const end = dayjs().format('YYYY-MM-DD HH:mm:ss')
  const start = dayjs().startOf('day').format('YYYY-MM-DD HH:mm:ss')
  dateRange.value = [start, end]
}

// 防止重复初始化标记
let chatHasInit = false

// 监听打开 dialog
watch(() => visible.value, (val) => {
  if (val) {
    if (!chatHasInit) {
      initDateRange()
      queryField.value = props.initialField || 'temp'
      chatHasInit = true
    } else {
      if (props.initialField) {
        queryField.value = props.initialField
      }
    }

    nextTick(() => {
      initChart()
      handleQuery()
    })
  } else {
    // 关闭时销毁图表实例，防止下次打开时使用旧的（已失效 DOM 的）实例
    disposeChart()
  }
})

// 监听 props.initialField
watch(() => props.initialField, (val) => {
  if (visible.value && val) {
    queryField.value = val
  }
})

// 监听筛选条件变化，自动重新获取数据
watch([dateRange, queryField], () => {
  if (visible.value) {
    handleQuery()
  }
})

const close = () => {
  visible.value = false
}

const initChart = () => {
  if (chartRef.value) {
    if (!chartInstance) {
      chartInstance = echarts.init(chartRef.value)
      window.addEventListener('resize', resizeChart)
    }
  }
}

const resizeChart = () => {
  chartInstance && chartInstance.resize()
}

const disposeChart = () => {
  if (chartInstance) {
    window.removeEventListener('resize', resizeChart)
    chartInstance.dispose()
    chartInstance = null
  }
}

onUnmounted(() => {
  disposeChart()
})

const handleQuery = async () => {
  if (!props.elinkUuid || !dateRange.value || dateRange.value.length < 2) return

  loading.value = true
  noData.value = false

  try {
    const params = {
      eLinkUUID: props.elinkUuid,
      startTime: dateRange.value[0],
      endTime: dateRange.value[1],
      field: queryField.value,
      interval: 1,
      stationId: Number(props.stationId)
    }

    const res = await request.get('/dashboard/v3/curve/fireData', { params })

    // 修正：根据截图，API返回 code: 200
    if (res && res.code === 200 && Array.isArray(res.data)) {
      updateChart(res.data)
    } else {
      handleEmptyData()
    }
  } catch (err) {
    console.error(err)
    handleEmptyData()
  } finally {
    loading.value = false
  }
}

const handleEmptyData = () => {
  noData.value = true
  if (chartInstance) {
    chartInstance.clear()
  }
}

const updateChart = (data) => {
  if (!Array.isArray(data) || data.length === 0) {
    handleEmptyData()
    return
  }

  const series = []
  const legendData = []
  const colors = ['#5470c6', '#91cc75', '#fac858', '#ee6666', '#73c0de', '#3ba272', '#fc8452', '#9a60b4', '#ea7ccc']

  data.forEach((item, index) => {
    // 数据结构确认：{ uuid: "...", eblockName: "1-1#eBlock", list: [...] }
    const name = item.eblockName || item.uuid || `Curve ${index + 1}`
    const list = item.list

    // 只有当 list 存在且有长度时，才生成一条有效的 series
    if (Array.isArray(list) && list.length > 0) {
      legendData.push(name)

      const dataPoints = list.map(point => {
        // record_time 为主，value 为值
        const timeStr = point.record_time || point.actual_time
        // 使用 dayjs 也可以，或者直接给字符串，echarts type: 'time' 也支持 ISO 串
        return [timeStr, point.value]
      })

      series.push({
        name: name,
        type: 'line',
        showSymbol: false,
        smooth: true,
        data: dataPoints,
        itemStyle: { color: colors[index % colors.length] }
      })
    }
  })

  if (series.length === 0) {
    handleEmptyData()
    return
  }

  const option = {
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'cross' }
    },
    legend: {
      data: legendData,
      top: 0,
      type: 'scroll' // 如果eblock很多，允许滚动
    },
    grid: {
      left: '3%',
      right: '4%',
      bottom: '3%',
      containLabel: true,
      top: 40
    },
    xAxis: {
      type: 'time',
      boundaryGap: false,
      axisLabel: {
        formatter: '{HH}:{mm}',
        hideOverlap: true
      },
      splitLine: {
        show: false
      }
    },
    yAxis: {
      type: 'value',
      scale: true,
      splitLine: {
        show: true,
        lineStyle: {
          type: 'dashed'
        }
      }
    },
    series: series
  }

  chartInstance.clear() // 先清空，避免旧数据残留
  chartInstance.setOption(option, true)
}

</script>

<style scoped lang="scss">
.fire-chart-detail-dialog {
  :deep(.v-overlay__content) {
    width: 100vw !important;
    max-width: 1920px !important;
    height: 950px !important;
    margin: 0 auto !important;
    border-radius: 30px 30px 0 0 !important;
  }
}

.dialog-wrapper {
  position: relative;
  width: 100%;
  height: 950px;
  padding: 20px 24px 0;
  background-color: #fff !important;
  background: url('@/assets/images/station-monitor/detail-dialog/detail-bg.svg') no-repeat center center;
  background-size: cover;
  border-radius: 30px 30px 0 0 !important;
  display: flex;
  flex-direction: column;
}

.dialog-title {
  display: flex;
  align-items: center;
  padding-left: 24px;
  margin-bottom: 20px;
  font-family: 'HarmonyOS Sans SC', sans-serif;
  font-size: 20px;
  color: rgba(46, 46, 46, 1);
  font-weight: normal;
  position: relative;
  flex-shrink: 0;
}

.back-btn {
  width: 98px;
  height: 40px;
  border-radius: 12px;
  border: 1px solid #C0CFDA;
  margin-right: 20px;
  font-size: 14px;
  color: #2E2E2E;
  justify-content: center;
  padding: 0;
}

.close-btn {
  position: absolute;
  top: 16px;
  right: 24px;
  z-index: 10;
  width: 48px;
  height: 48px;
  color: #2E2E2E;
}

.dialog-main {
  height: calc(100% - 83px);
  background: rgba(246, 251, 255, 1);
  border-radius: 30px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* 筛选栏样式 - 复刻 AlertManagement.vue */
.filter-bar {
  display: flex;
  align-items: center;
  margin-bottom: 20px;
  background: #fff;
  padding: 16px 20px;
  border-radius: 16px;
  gap: 20px;

  .filter-item {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-shrink: 0;
    height: 40px;
  }

  .filter-label {
    font-family: 'PingFang SC', sans-serif;
    font-size: 14px;
    font-weight: 400;
    color: #2e2e2e;
    white-space: nowrap;
    flex-shrink: 0;
    line-height: 40px;
    height: 40px;
    display: flex;
    align-items: center;
  }
}

.filter-date-picker {
  width: 380px !important; // 增加宽度以容纳 datetimerange
  min-width: 380px !important;
  max-width: 380px !important;

  :deep(.el-input__wrapper) {
    width: 100% !important;
    background: #fff;
    border: 1px solid rgba(192, 207, 218, 0.3);
    border-radius: 20px;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
    padding: 0 16px;
    min-height: 40px;
    height: 40px;
    transition: border-color 0.2s;

    &.is-focus {
      border-color: #3a68ff;
    }
  }

  :deep(.el-input__inner) {
    font-family: "HarmonyOS Sans SC", sans-serif;
    font-size: 14px;
    color: #2E2E2E;
    height: 40px;
    line-height: 40px;
  }
}

:deep(.filter-date-picker.el-range-editor) {
  width: 380px !important; // 确保 range-editor 宽度也足够
  min-width: 380px !important;
  max-width: 380px !important;
  background: #fff;
  border: 1px solid rgba(192, 207, 218, 0.3);
  border-radius: 20px;
  align-items: center;
  margin: 0;
  display: flex;
  box-shadow: 0 1px 2px #0000000d;
  height: 40px !important;
  min-height: 40px !important;
  max-height: 40px !important;
  padding: 0 10px;

  &.is-active {
    border-color: #3a68ff;
  }
}

.filter-select {
  width: 230px;
  min-width: 230px;

  :deep(.v-field) {
    min-height: 40px !important;
    height: 40px !important;
    max-height: 40px !important;
    background: #fff;
    border: 1px solid rgba(192, 207, 218, 0.3);
    border-radius: 20px;
    display: flex;
    align-items: center;
    margin: 0;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  }

  :deep(.v-field__input) {
    padding: 8px 0px 8px 16px;
    font-family: "HarmonyOS Sans SC", sans-serif;
    font-size: 14px;
    display: flex;
    align-items: center;
  }

  :deep(.v-field__outline) {
    display: none;
  }

  :deep(.v-select__selection) {
    font-family: "HarmonyOS Sans SC", sans-serif;
    font-size: 14px;
    color: #2E2E2E;
    line-height: 24px;
    display: flex;
    align-items: center;
  }

  :deep(.v-field__append-inner .v-icon) {
    font-size: 16px !important;
    width: 16px !important;
    height: 16px !important;
    color: #2E2E2E !important;
    opacity: 0.22;
  }
}

.filter-actions {
  // margin-left: auto; // Align to right
}

.query-btn {
  width: 98px;
  height: 40px !important;
  min-height: 40px !important;
  border-radius: 12px !important;
  font-family: "HarmonyOS Sans SC", sans-serif;
  font-size: 14px;
  font-weight: 400;
  background: #3a68ff !important;
  color: #fff !important;
  text-transform: none !important;
  padding: 0 !important;
  border: none !important;
  box-shadow: none !important;
  margin: 0;
  display: flex;
  align-items: center;
  justify-content: center;

  .query-icon {
    width: 13px;
    height: 13px;
    margin-inline-end: 6px;
  }
}

.chart-wrapper {
  flex: 1;
  position: relative;
  background: #fff;
  border-radius: 16px;
  padding: 16px;
  min-height: 0;
}

.chart-container {
  width: 100%;
  height: 100%;
}

.loading-overlay,
.no-data {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  background: rgba(255, 255, 255, 0.8);
  z-index: 10;
}

.no-data {
  color: #999;
  font-size: 14px;
  background: transparent;
}
</style>

<style>
/* 强制提升日期选择弹窗层级 */
.fire-chart-date-popper {
  z-index: 10000 !important;
}

.fire-chart-filter-select-menu {
  z-index: 10000 !important;
}
</style>
